import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import {
  applyFaceExpression,
  applyShowSkeleton,
  applySkinHex,
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';
import useGLTFWithCredentials from '@cartwhl/3d/r3f/hooks/useGLTFWithCredentials';

import { characterPickerStore, sceneSettingsStore } from 'stores';

export default function useLoadCharacter(
  forceSkeleton?: boolean,
  setLoading?: (loading: boolean) => void,
) {
  const {
    characterSettings: { skinHex, faceExpression, charUploadID, showSkeleton },
  } = useSnapshot(sceneSettingsStore.state);

  const { activeCharacter } = useSnapshot(characterPickerStore.state);

  // Ensure the active character ID in both stores is synchronized
  useEffect(() => {
    if (charUploadID !== characterPickerStore.state.activeCharacterID) {
      characterPickerStore.setActiveCharacterID(charUploadID);
    }
  }, [charUploadID]);

  const character = activeCharacter;
  const characterID = character?.PK ?? charUploadID;

  const meshData = useGLTFWithCredentials(character?.meshPath, setLoading, [
    hideMeshMaterials,
    turnOnShadows,
    disableFrustumCulled,
  ]);
  const rotationOffsets = character?.rotationOffsets;

  useEffect(() => {
    if (meshData) {
      applySkinHex(skinHex, characterID)(meshData);
    }
  }, [characterID, meshData, skinHex]);

  useEffect(() => {
    if (meshData) {
      applyFaceExpression(faceExpression)(meshData);
    }
  }, [faceExpression, meshData]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, forceSkeleton || showSkeleton);
    }
  }, [forceSkeleton, showSkeleton, meshData]);

  return { meshData, rotationOffsets };
}
